import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { ProductSearchCombobox } from '@/components/brand-products/ProductSearchCombobox';
import { BusinessStageSelector } from '@/components/business-stages/BusinessStageSelector';
import { PersonaSelector } from '@/components/engagement-points/PersonaSelector';
import { useEngagementPointDialog } from '@/contexts/EngagementPointDialogContext';
import { useEngagementPoints } from '@/hooks/useEngagementPoints';
import { useBrandSelection } from '@/hooks/useBrandSelection';
import { useAuth } from '@/hooks/useAuth';

interface EngagementPointFormData {
  product_id: string | null;
  business_stage_id: string | null;
  persona_ids: string[];
  page_name: string;
  route: string;
  form_name: string;
  form_fields: string[];
  webhook_url: string;
  webhook_enabled: boolean;
  description: string;
  is_active: boolean;
}

export function GlobalEngagementPointDialog() {
  const { isOpen, editingEngagementPoint, closeEngagementPointDialog } = useEngagementPointDialog();
  const { currentBrandId } = useBrandSelection();
  const { user } = useAuth();
  const brandId = currentBrandId || '';
  
  const { createEngagementPoint, updateEngagementPoint } = useEngagementPoints(brandId);

  const [formData, setFormData] = useState<EngagementPointFormData>({
    product_id: null,
    business_stage_id: null,
    persona_ids: [],
    page_name: '',
    route: '',
    form_name: '',
    form_fields: [],
    webhook_url: '',
    webhook_enabled: false,
    description: '',
    is_active: true,
  });
  const [newField, setNewField] = useState('');

  const isEditing = !!editingEngagementPoint;

  useEffect(() => {
    if (editingEngagementPoint) {
      setFormData({
        product_id: editingEngagementPoint.product_id,
        business_stage_id: editingEngagementPoint.business_stage_id,
        persona_ids: editingEngagementPoint.persona_ids || [],
        page_name: editingEngagementPoint.page_name,
        route: editingEngagementPoint.route,
        form_name: editingEngagementPoint.form_name || '',
        form_fields: editingEngagementPoint.form_fields || [],
        webhook_url: editingEngagementPoint.webhook_url || '',
        webhook_enabled: editingEngagementPoint.webhook_enabled,
        description: editingEngagementPoint.description || '',
        is_active: editingEngagementPoint.is_active,
      });
    } else {
      setFormData({
        product_id: null,
        business_stage_id: null,
        persona_ids: [],
        page_name: '',
        route: '',
        form_name: '',
        form_fields: [],
        webhook_url: '',
        webhook_enabled: false,
        description: '',
        is_active: true,
      });
    }
    setNewField('');
  }, [editingEngagementPoint, isOpen]);

  const handleAddField = () => {
    if (newField.trim() && !formData.form_fields.includes(newField.trim())) {
      setFormData((prev) => ({
        ...prev,
        form_fields: [...prev.form_fields, newField.trim()],
      }));
      setNewField('');
    }
  };

  const handleRemoveField = (field: string) => {
    setFormData((prev) => ({
      ...prev,
      form_fields: prev.form_fields.filter((f) => f !== field),
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddField();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing && editingEngagementPoint) {
      updateEngagementPoint.mutate(
        { id: editingEngagementPoint.id, ...formData },
        { onSuccess: closeEngagementPointDialog }
      );
    } else {
      if (!user?.id || !brandId) return;
      createEngagementPoint.mutate(
        { brand_id: brandId, user_id: user.id, ...formData },
        { onSuccess: closeEngagementPointDialog }
      );
    }
  };

  const isSubmitting = createEngagementPoint.isPending || updateEngagementPoint.isPending;

  if (!brandId) return null;

  return (
    <Dialog open={isOpen} onOpenChange={closeEngagementPointDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitleWithBug
          popupName={isEditing ? 'Edit Engagement Point' : 'Add Engagement Point'}
          popupContext="/engagement-points"
          isOpen={isOpen}
        >
          {isEditing ? 'Edit Engagement Point' : 'Add Engagement Point'}
        </DialogTitleWithBug>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Page Name */}
          <div className="space-y-2">
            <Label htmlFor="page_name">Page Name *</Label>
            <Input
              id="page_name"
              value={formData.page_name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, page_name: e.target.value }))
              }
              placeholder="e.g., Lisa, Event Seekers, Contact Us"
              required
            />
          </div>

          {/* Route */}
          <div className="space-y-2">
            <Label htmlFor="route">Route *</Label>
            <Input
              id="route"
              value={formData.route}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, route: e.target.value }))
              }
              placeholder="e.g., /lisa, /event-seekers, /contact"
              required
            />
          </div>

          {/* Product */}
          <div className="space-y-2">
            <Label>Linked Product</Label>
            <ProductSearchCombobox
              brandId={brandId}
              value={formData.product_id}
              onChange={(productId) =>
                setFormData((prev) => ({ ...prev, product_id: productId }))
              }
              placeholder="Select a product to link..."
            />
          </div>

          {/* Business Stage */}
          <div className="space-y-2">
            <Label>Business Stage</Label>
            <BusinessStageSelector
              brandId={brandId}
              value={formData.business_stage_id}
              onChange={(stageId) =>
                setFormData((prev) => ({ ...prev, business_stage_id: stageId }))
              }
              placeholder="Select a business stage..."
            />
          </div>

          {/* Target Personas */}
          <div className="space-y-2">
            <Label>Target Personas</Label>
            <PersonaSelector
              brandId={brandId}
              selectedPersonaIds={formData.persona_ids}
              onChange={(personaIds) =>
                setFormData((prev) => ({ ...prev, persona_ids: personaIds }))
              }
            />
          </div>

          {/* Form Name */}
          <div className="space-y-2">
            <Label htmlFor="form_name">Form Name</Label>
            <Input
              id="form_name"
              value={formData.form_name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, form_name: e.target.value }))
              }
              placeholder="e.g., Sign Up Modal, Plan Event Modal"
            />
          </div>

          {/* Form Fields */}
          <div className="space-y-2">
            <Label>Form Fields</Label>
            <div className="flex gap-2">
              <Input
                value={newField}
                onChange={(e) => setNewField(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add a field name and press Enter"
              />
              <Button type="button" variant="secondary" onClick={handleAddField}>
                Add
              </Button>
            </div>
            {formData.form_fields.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.form_fields.map((field) => (
                  <Badge key={field} variant="secondary" className="gap-1">
                    {field}
                    <button
                      type="button"
                      onClick={() => handleRemoveField(field)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Webhook URL */}
          <div className="space-y-2">
            <Label htmlFor="webhook_url">Webhook URL</Label>
            <Input
              id="webhook_url"
              value={formData.webhook_url}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, webhook_url: e.target.value }))
              }
              placeholder="https://your-webhook-endpoint.com/handler"
            />
          </div>

          {/* Webhook Enabled */}
          <div className="flex items-center justify-between">
            <Label htmlFor="webhook_enabled">Webhook Enabled</Label>
            <Switch
              id="webhook_enabled"
              checked={formData.webhook_enabled}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, webhook_enabled: checked }))
              }
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Optional notes about this engagement point..."
              rows={3}
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between">
            <Label htmlFor="is_active">Active</Label>
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_active: checked }))
              }
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeEngagementPointDialog}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
